import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, CheckCheck, Trash2, Bell, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Notification } from '@/hooks/useNotifications';
import { NotificationRefreshButton } from './NotificationRefreshButton';

interface NotificationDropdownProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onRemove: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}

const getNotificationTypeColor = (type: Notification['type']) => {
  switch (type) {
    case 'success': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'error': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  }
};

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onRemove,
  onClearAll,
  onClose
}) => {
  const navigate = useNavigate();
  const unreadNotifications = notifications.filter(n => !n.read);

  const handleNotificationClick = (notification: Notification) => {
    // Marcar como leída al hacer clic
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    // Ejecutar acción si existe
    if (notification.action) {
      switch (notification.action.type) {
        case 'navigate':
          navigate(notification.action.target);
          onClose(); // Cerrar dropdown después de navegar
          break;
        case 'external':
          window.open(notification.action.target, '_blank');
          break;
        case 'modal':
          // Aquí se podría abrir un modal específico
          console.log('Open modal:', notification.action.target);
          break;
      }
    }
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-background border border-border rounded-lg shadow-lg z-50">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Notificaciones</h3>
        <div className="flex items-center gap-2">
          <NotificationRefreshButton />
          {unreadNotifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkAllAsRead}
              className="text-xs"
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Marcar todas
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No tienes notificaciones</p>
        </div>
      ) : (
        <>
          <ScrollArea className="max-h-96">
            <div className="p-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 rounded-lg mb-2 transition-all cursor-pointer hover:bg-accent/70 ${
                    notification.read 
                      ? 'bg-muted/50' 
                      : 'bg-accent/50 border border-accent'
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm text-foreground truncate">
                          {notification.title}
                        </h4>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${getNotificationTypeColor(notification.type)}`}
                        >
                          {notification.type}
                        </Badge>
                        {notification.action && (
                          <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(notification.timestamp, { 
                            addSuffix: true, 
                            locale: es 
                          })}
                        </p>
                        {notification.action && (
                          <span className="text-xs text-primary font-medium">
                            Clic para abrir
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onMarkAsRead(notification.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(notification.id)}
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {notifications.length > 0 && (
            <>
              <Separator />
              <div className="p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearAll}
                  className="w-full text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Limpiar todas
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};